import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { JwtBearerInterceptor } from './shared/interceptors/jwt-bearer.interceptor';
import { TimeoutInterceptor } from './shared/interceptors/timeout.interceptor';
import { StockAuditInterceptor } from './shared/interceptors/stock-audit.interceptor';

registerLocaleData(localePt);

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './components/login/login.component';
import { UsersComponent } from './components/users/users.component';
import { UserViewModalComponent } from './components/users/user-view-modal.component';
import { SharedModule } from './shared/shared.module';
import { ResetPasswordCodeComponent } from './components/reset-password-code/reset-password-code.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { UserRegistrationComponent } from './components/user-registration/user-registration.component';
import { SuppliersComponent } from './components/shopping/suppliers/suppliers.component';
import { SuppliersFormComponent } from './components/shopping/suppliers/suppliers-form/suppliers-form.component';
import { StaffComponent } from './components/staff/staff.component';
import { StaffFormComponent } from './components/staff/staff-form/staff-form.component';
import { StaffDetailsComponent } from './components/staff/staff-details/staff-details.component';
import { StaffFormViewComponent } from './components/staff/staff-form-view/staff-form-view.component';
import { DepartmentsComponent } from './components/departments/departments.component';
import { DepartmentFormComponent } from './components/departments/department-form/department-form.component';
import { PositionsComponent } from './components/departments/positions/positions.component';
import { PositionFormComponent } from './components/departments/positions/position-form/position-form.component';
import { VehiclesComponent } from './components/vehicles/vehicles.component';
import { VehicleFormComponent } from './components/vehicles/vehicle-form/vehicle-form.component';
import { CustomersComponent } from './components/commercial/customers/customers.component';
import { CustomerFormComponent } from './components/commercial/customers/customer-form/customer-form.component';
import { CustomerViewComponent } from './components/commercial/customers/customer-view/customer-view.component';
import { DashComponentUserComponent } from './components/dash-component-user/dash-component-user.component';
import { ScheduleComponent } from './components/admin/schedule/schedule.component';
import { PriceApprovalRangesListComponent } from './components/price-approval/price-approval-ranges-list/price-approval-ranges-list.component';
import { PriceApprovalRangeFormComponent } from './components/price-approval/price-approval-range-form/price-approval-range-form.component';
import { GroupsListComponent } from './components/admin/groups-list/groups-list.component';
import { GroupCreateComponent } from './components/admin/group-create/group-create.component';
import { ProfilesListComponent } from './components/admin/profiles-list/profiles-list.component';
import { ProfileCreateComponent } from './components/admin/profile-create/profile-create.component';
import { CostCentersComponent } from './components/financial/cost-centers/cost-centers.component';
import { CostCenterFormComponent } from './components/financial/cost-centers/cost-center-form/cost-center-form.component';
import { CostCenterBudgetProposalsComponent } from './components/financial/cost-center-budget-proposals/cost-center-budget-proposals.component';
import { CostCenterBudgetProposalViewComponent } from './components/financial/cost-center-budget-proposals/cost-center-budget-proposal-view.component';
import { CostCenterViewComponent } from './components/financial/cost-centers/cost-center-view/cost-center-view.component';
import { CostCenterConsolidatedStatisticsComponent } from './components/financial/cost-centers/cost-center-consolidated-statistics/cost-center-consolidated-statistics.component';
import { ControladoriaNavComponent } from './components/financial/controladoria/controladoria-nav.component';
import { WarehousesComponent } from './components/stock/warehouses/warehouses.component';
import { WarehouseFormComponent } from './components/stock/warehouses/warehouse-form/warehouse-form.component';
import { StockComponent } from './components/stock/stock/stock.component';
import { StockFormComponent } from './components/stock/stock/stock-form/stock-form.component';
import { StockViewComponent } from './components/stock/stock/stock-view/stock-view.component';
import { StockWarehouseModalComponent } from './components/stock/stock/stock-warehouse-modal/stock-warehouse-modal.component';
import { ItemsComponent } from './components/stock/items/items.component';
import { ItemFormComponent } from './components/stock/items/item-form/item-form.component';
import { ItemViewComponent } from './components/stock/items/item-view/item-view.component';
import { ItemTransferModalComponent } from './components/stock/items/item-transfer-modal/item-transfer-modal.component';
import { AccountsPayableComponent } from './components/financial/accounts-payable/accounts-payable.component';
import { AccountsPayableFormComponent } from './components/financial/accounts-payable/accounts-payable-form/accounts-payable-form.component';
import { AccountsPayableViewComponent } from './components/financial/accounts-payable/accounts-payable-view/accounts-payable-view.component';
import { DepositRefundAccountsPayableComponent } from './components/financial/deposit-refund/deposit-refund-accounts-payable.component';
import { AccountsReceivableComponent } from './components/financial/accounts-receivable/accounts-receivable.component';
import { AccountsReceivableFormComponent } from './components/financial/accounts-receivable/accounts-receivable-form/accounts-receivable-form.component';
import { AccountsReceivableViewComponent } from './components/financial/accounts-receivable/accounts-receivable-view/accounts-receivable-view.component';
import { BankStatementsComponent } from './components/financial/bank-statements/bank-statements.component';
import { BankTransactionsComponent } from './components/financial/bank-transactions/bank-transactions.component';
import { CardOperatorComponent } from './components/financial/card-operator/card-operator.component';
import { CashflowComponent } from './components/financial/cashflow/cashflow.component';
import { CashflowViewComponent } from './components/financial/cashflow/cashflow-view/cashflow-view.component';
import { FinancialReportsNavComponent } from './components/financial/financial-reports-nav/financial-reports-nav.component';
import { FinancialReportsComponent } from './components/financial/financial-reports/financial-reports.component';
import { ApPositionSummaryComponent } from './components/financial/ap-position-summary/ap-position-summary.component';
import { TrafficFinesComponent } from './components/financial/traffic-fines/traffic-fines.component';
import { TrafficFinesUnlinkedComponent } from './components/financial/traffic-fines/traffic-fines-unlinked.component';
import { TrafficFinesDashboardComponent } from './components/financial/traffic-fines/traffic-fines-dashboard.component';
import { TrafficFineDetailComponent } from './components/financial/traffic-fine-detail/traffic-fine-detail.component';
import { TrafficFineEmailPanelComponent } from './components/financial/traffic-fine-detail/traffic-fine-email-panel.component';
import { VehicleIpvaLicensingListComponent } from './components/financial/vehicle-ipva-licensing/vehicle-ipva-licensing-list.component';
import { VehicleIpvaLicensingDetailComponent } from './components/financial/vehicle-ipva-licensing/vehicle-ipva-licensing-detail.component';
import { VehicleIpvaLicensingPendenciesComponent } from './components/financial/vehicle-ipva-licensing/vehicle-ipva-licensing-pendencies.component';
import { AgreementsComponent } from './components/financial/agreements/agreements.component';
import { AgreementFormComponent } from './components/financial/agreements/agreement-form/agreement-form.component';
import { PurchaseOrdersComponent } from './components/shopping/purchase-orders/purchase-orders.component';
import { PurchaseOrderFormComponent } from './components/shopping/purchase-orders/purchase-order-form.component';
import { PurchaseRequestFormComponent } from './components/shopping/purchase-orders/purchase-request-form.component';
import { PurchaseOrderDetailsComponent } from './components/shopping/purchase-orders/purchase-order-details.component';
import { PurchaseOrderApprovalComponent } from './components/shopping/purchase-orders/purchase-order-approval.component';
import { PurchaseOrderQuotationsComponent } from './components/shopping/purchase-orders/purchase-order-quotations/purchase-order-quotations.component';
import { PurchaseOrderQuotationCreateComponent } from './components/shopping/purchase-orders/purchase-order-quotations/purchase-order-quotation-create.component';
import { VehicleRentalsComponent } from './components/commercial/vehicle-rentals/vehicle-rentals.component';
import { VehicleRentalFormComponent } from './components/commercial/vehicle-rentals/vehicle-rental-form/vehicle-rental-form.component';
import { VehicleRentalDetailsComponent } from './components/commercial/vehicle-rentals/vehicle-rental-details/vehicle-rental-details.component';
import { VehicleRentalApprovalComponent } from './components/commercial/vehicle-rentals/vehicle-rental-approval/vehicle-rental-approval.component';
import { StockMovementsComponent } from './components/stock/stock-movements/stock-movements.component';
import { StockAlertsComponent } from './components/stock/stock-alerts/stock-alerts.component';
import { MovementApprovalsComponent } from './components/stock/movement-approvals/movement-approvals.component';
import { StockMovementsFormComponent } from './components/stock/stock-movements/stock-movements-form/stock-movements-form.component';
import { StockEntryReceiptComponent } from './components/stock/stock-movements/stock-entry-receipt/stock-entry-receipt.component';
import { StockRequestDetailsComponent } from './components/stock/stock-requests/stock-request-details.component';
import { ChecklistListComponent } from './components/checklist/checklist-list/checklist-list.component';
import { ChecklistFormComponent } from './components/checklist/checklist-form/checklist-form.component';
import { TemplatesComponent } from './components/documents/templates/templates.component';
import { TemplateVersionsComponent } from './components/documents/templates/template-versions.component';
import { TemplateEditorComponent } from './components/documents/templates/template-editor.component';
import { ProductsComponent } from './components/products/products.component';
import { ProductFormComponent } from './components/products/product-form/product-form.component';
import { ProductViewComponent } from './components/products/product-view/product-view.component';
import { ContractsComponent } from './components/contracts/contracts.component';
import { ContractFormComponent } from './components/contracts/contract-form/contract-form.component';
import { ContractViewComponent } from './components/contracts/contract-view/contract-view.component';
import { ContractAccountsReceivablePanelComponent } from './components/contracts/contract-accounts-receivable-panel/contract-accounts-receivable-panel.component';
import { ContractFreezeModalComponent } from './shared/components/contract-freeze-modal/contract-freeze-modal.component';
import { ContractRenewComponent } from './components/contracts/contract-renew/contract-renew.component';
import { ContractReplaceVehicleComponent } from './components/contracts/contract-replace-vehicle/contract-replace-vehicle.component';
import { ServiceOrdersListComponent } from './components/service-orders/service-orders-list/service-orders-list.component';
import { ServiceOrdersConsultaComponent } from './components/service-orders/service-orders-consulta/service-orders-consulta.component';
import { ServiceOrderConsultaViewModalComponent } from './components/service-orders/service-order-consulta-view-modal/service-order-consulta-view-modal.component';
import { ServiceOrderFormComponent } from './components/service-orders/service-order-form/service-order-form.component';
import { AprovalServiceComponent } from './components/service-orders/aproval-service/aproval-service.component';
import { ServiceOrderConfigComponent } from './components/service-orders/service-order-config/service-order-config.component';
import { ServiceOrderConfigViewEditComponent } from './components/service-orders/service-order-config/service-order-config-view-edit/service-order-config-view-edit.component';
import { BudgetServiceOrdersComponent } from './components/service-orders/budget-service-orders/budget-service-orders.component';
import { BudgetServiceOrderDetailComponent } from './components/service-orders/budget-service-order-detail/budget-service-order-detail.component';
import { BudgetCommentsComponent } from './components/service-orders/budget-collaboration/budget-comments.component';
import { CollaborationCommentsComponent } from './components/service-orders/budget-collaboration/collaboration-comments.component';
import { BudgetEvidencesComponent } from './components/service-orders/budget-collaboration/budget-evidences.component';
import { BudgetDetailItemsComponent } from './components/service-orders/budget-collaboration/budget-detail-items.component';
import { BudgetServiceOrderCreateComponent } from './components/service-orders/budget-service-order-create/budget-service-order-create.component';
import { CustomerSelectionModalComponent } from './components/service-orders/customer-selection-modal/customer-selection-modal.component';
import { VehicleSelectionModalComponent } from './components/service-orders/vehicle-selection-modal/vehicle-selection-modal.component';
import { DirecionamentoModalComponent } from './components/service-orders/direcionamento-modal/direcionamento-modal.component';
import { ServiceOrderCloseModalComponent } from './components/service-orders/service-order-close-modal/service-order-close-modal.component';
import { MechanicSelectionModalComponent } from './components/service-orders/mechanic-selection-modal/mechanic-selection-modal.component';
import { WarehouseSelectionModalComponent } from './components/service-orders/warehouse-selection-modal/warehouse-selection-modal.component';
import { ProviderSelectionModalComponent } from './components/service-orders/provider-selection-modal/provider-selection-modal.component';
import { ServicesListComponent } from './components/operations/services/services-list/services-list.component';
import { ServicesFormComponent } from './components/operations/services/services-form/services-form.component';
import { ServicesDetailsComponent } from './components/operations/services/services-details/services-details.component';
import { PreventiveMaintenanceListComponent } from './components/operations/preventive-maintenance/preventive-maintenance-list/preventive-maintenance-list.component';
import { PreventiveMaintenanceFormComponent } from './components/operations/preventive-maintenance/preventive-maintenance-form/preventive-maintenance-form.component';
import { PreventiveMaintenanceDetailsComponent } from './components/operations/preventive-maintenance/preventive-maintenance-details/preventive-maintenance-details.component';
import { PreventiveMaintenanceHistoryListComponent } from './components/operations/preventive-maintenance/preventive-maintenance-history-list/preventive-maintenance-history-list.component';
import { ExternalServiceProvidersComponent } from './components/b2b/external-service-providers/external-service-providers.component';
import { ExternalServiceProvidersFormComponent } from './components/b2b/external-service-providers-form/external-service-providers-form.component';
import { ExternalServiceProvidersViewComponent } from './components/b2b/external-service-providers-view/external-service-providers-view.component';
import { B2bContractsComponent } from './components/b2b/b2b-contracts/b2b-contracts.component';
import { B2bContractsViewComponent } from './components/b2b/b2b-contracts-view/b2b-contracts-view.component';
import { B2bContractsFormComponent } from './components/b2b/b2b-contracts-form/b2b-contracts-form.component';
import { B2bContractsDriversComponent } from './components/b2b/b2b-contracts-drivers/b2b-contracts-drivers.component';
import { B2bContractsDriverViewComponent } from './components/b2b/b2b-contracts-driver-view/b2b-contracts-driver-view.component';
import { B2bContractsDriverFormComponent } from './components/b2b/b2b-contracts-driver-form/b2b-contracts-driver-form.component';
import { B2bDriverDocumentsComponent } from './components/b2b/b2b-driver-documents/b2b-driver-documents.component';
import { B2bContractsItemsComponent } from './components/b2b/b2b-contracts-items/b2b-contracts-items.component';
import { B2bContractsVehiclesComponent } from './components/b2b/b2b-contracts-vehicles/b2b-contracts-vehicles.component';
import { B2bContractsItemViewComponent } from './components/b2b/b2b-contracts-item-view/b2b-contracts-item-view.component';
import { B2bContractsVehicleViewComponent } from './components/b2b/b2b-contracts-vehicle-view/b2b-contracts-vehicle-view.component';
import { B2bContractsItemFormComponent } from './components/b2b/b2b-contracts-item-form/b2b-contracts-item-form.component';
import { B2bContractsDriverAssignmentComponent } from './components/b2b/b2b-contracts-driver-assignment/b2b-contracts-driver-assignment.component';
import { B2bPortalAccessCenterComponent } from './components/b2b/b2b-portal-access-center/b2b-portal-access-center.component';
import { B2bPortalUserFormComponent } from './components/b2b/b2b-portal-user-form/b2b-portal-user-form.component';
import { B2bPortalUsersHubComponent } from './components/b2b/b2b-portal-users-hub/b2b-portal-users-hub.component';
import { B2bContractsDashboardComponent } from './components/b2b/b2b-contracts-dashboard/b2b-contracts-dashboard.component';
import { B2bPortfolioHealthComponent } from './components/b2b/b2b-portfolio-health/b2b-portfolio-health.component';
import { B2bOperationalNavComponent } from './components/b2b/b2b-operational-nav/b2b-operational-nav.component';
import { B2bClaimsComponent } from './components/b2b/b2b-claims/b2b-claims.component';
import { B2bClaimsFormComponent } from './components/b2b/b2b-claims-form/b2b-claims-form.component';
import { B2bChargesComponent } from './components/b2b/b2b-charges/b2b-charges.component';
import { B2bInvoicesComponent } from './components/b2b/b2b-invoices/b2b-invoices.component';
import { B2bInvoiceDetailComponent } from './components/b2b/b2b-invoice-detail/b2b-invoice-detail.component';
import { B2bPriceTableComponent } from './components/b2b/b2b-price-table/b2b-price-table.component';
import { B2bBillingClosingComponent } from './components/financial/b2b-billing-closing/b2b-billing-closing.component';
import { B2bReservationsComponent } from './components/b2b/b2b-reservations/b2b-reservations.component';
import { B2bAccessoriesComponent } from './components/b2b/b2b-accessories/b2b-accessories.component';
import { B2bBillingPeriodsComponent } from './components/b2b/b2b-billing-periods/b2b-billing-periods.component';
import { B2bTrafficFinesComponent } from './components/b2b/b2b-traffic-fines/b2b-traffic-fines.component';
import { B2bSubstitutionsComponent } from './components/b2b/b2b-substitutions/b2b-substitutions.component';
import { B2bReturnEventsComponent } from './components/b2b/b2b-return-events/b2b-return-events.component';
import { B2bFleetDeliveriesComponent } from './components/b2b/b2b-fleet-deliveries/b2b-fleet-deliveries.component';
import { B2bOperationalDashboardComponent } from './components/b2b/b2b-operational-dashboard/b2b-operational-dashboard.component';
import { B2bServiceOrdersComponent } from './components/b2b/b2b-service-orders/b2b-service-orders.component';
import { B2bContractPlateSelectComponent } from './components/b2b/b2b-contract-plate-select/b2b-contract-plate-select.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { StatusManagementComponent } from './components/config/status-management/status-management.component';
import { StatusManagementFormComponent } from './components/config/status-management-form/status-management-form.component';
import { SystemLockComponent } from './components/config/system-lock/system-lock.component';
import { NumberMaskDirective } from './directives/number-mask.directive';
import { CurrencyShiftMaskDirective } from './directives/currency-shift-mask.directive';
import { PlateMaskDirective } from './directives/plate-mask.directive';
import { AppointmentsComponent } from './components/appointments/appointments.component';
import { AppointmentThirdPartyOsFormComponent } from './components/appointments/appointment-third-party-os-form/appointment-third-party-os-form.component';
import { B2bAppointmentBookingFormComponent } from './components/appointments/b2b-booking/b2b-appointment-booking-form.component';
import { HrPayrollCenterComponent } from './components/hr-payroll-center/hr-payroll-center.component';
import { Loca9AppComponent } from './components/commercial/loca9-app/loca9-app.component';
import { AppointmentsExternalComponent } from './components/appointments-external/appointments-external.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    UsersComponent,
    UserViewModalComponent,
    ResetPasswordCodeComponent,
    ResetPasswordComponent,
    UserRegistrationComponent,
    SuppliersComponent,
    SuppliersFormComponent,
    StaffComponent,
    StaffFormComponent,
    StaffDetailsComponent,
    StaffFormViewComponent,
    DepartmentsComponent,
    DepartmentFormComponent,
    PositionsComponent,
    PositionFormComponent,
    VehiclesComponent,
    VehicleFormComponent,
    CustomersComponent,
    CustomerFormComponent,
    CustomerViewComponent,
    DashComponentUserComponent,
    AppointmentsComponent,
    AppointmentThirdPartyOsFormComponent,
    B2bAppointmentBookingFormComponent,
    GroupsListComponent,
    GroupCreateComponent,
    ProfilesListComponent,
    ProfileCreateComponent,
    CostCentersComponent,
    CostCenterFormComponent,
    CostCenterBudgetProposalsComponent,
    CostCenterBudgetProposalViewComponent,
    CostCenterViewComponent,
    CostCenterConsolidatedStatisticsComponent,
    ControladoriaNavComponent,
    WarehousesComponent,
    WarehouseFormComponent,
    StockComponent,
    StockFormComponent,
    StockViewComponent,
    StockWarehouseModalComponent,
    ItemsComponent,
    ItemFormComponent,
    ItemViewComponent,
    ItemTransferModalComponent,
    AccountsPayableComponent,
    AccountsPayableFormComponent,
    AccountsPayableViewComponent,
    DepositRefundAccountsPayableComponent,
    AccountsReceivableComponent,
    AccountsReceivableFormComponent,
    AccountsReceivableViewComponent,
    PurchaseOrdersComponent,
    PurchaseOrderFormComponent,
    PurchaseRequestFormComponent,
    PurchaseOrderDetailsComponent,
    PurchaseOrderApprovalComponent,
    PurchaseOrderQuotationsComponent,
    PurchaseOrderQuotationCreateComponent,
    VehicleRentalsComponent,
    VehicleRentalFormComponent,
    VehicleRentalDetailsComponent,
    VehicleRentalApprovalComponent,
    StockMovementsComponent,
    StockAlertsComponent,
    MovementApprovalsComponent,
    StockMovementsFormComponent,
    StockEntryReceiptComponent,
    StockRequestDetailsComponent,
    ChecklistListComponent,
    ChecklistFormComponent,
    TemplatesComponent,
    TemplateVersionsComponent,
    TemplateEditorComponent,
    ProductsComponent,
    ProductFormComponent,
    ProductViewComponent,
    ContractsComponent,
    ContractFormComponent,
    ContractViewComponent,
    ContractAccountsReceivablePanelComponent,
    ContractFreezeModalComponent,
    ContractRenewComponent,
    ContractReplaceVehicleComponent,
    ServiceOrdersListComponent,
    ServiceOrdersConsultaComponent,
    ServiceOrderConsultaViewModalComponent,
    ServiceOrderFormComponent,
    AprovalServiceComponent,
    ServiceOrderConfigComponent,
    ServiceOrderConfigViewEditComponent,
    BudgetServiceOrdersComponent,
    BudgetServiceOrderDetailComponent,
    BudgetCommentsComponent,
    CollaborationCommentsComponent,
    BudgetEvidencesComponent,
    BudgetDetailItemsComponent,
    BudgetServiceOrderCreateComponent,
    CustomerSelectionModalComponent,
    VehicleSelectionModalComponent,
    DirecionamentoModalComponent,
    ServiceOrderCloseModalComponent,
    MechanicSelectionModalComponent,
    WarehouseSelectionModalComponent,
    ProviderSelectionModalComponent,
    ServicesListComponent,
    ServicesFormComponent,
    ServicesDetailsComponent,
    BankStatementsComponent,
    BankTransactionsComponent,
    CardOperatorComponent,
    CashflowComponent,
    FinancialReportsNavComponent,
    FinancialReportsComponent,
    ApPositionSummaryComponent,
    CashflowViewComponent,
    TrafficFinesComponent,
    TrafficFinesUnlinkedComponent,
    TrafficFinesDashboardComponent,
    TrafficFineDetailComponent,
    TrafficFineEmailPanelComponent,
    VehicleIpvaLicensingListComponent,
    VehicleIpvaLicensingDetailComponent,
    VehicleIpvaLicensingPendenciesComponent,
    AgreementsComponent,
    AgreementFormComponent,
    PreventiveMaintenanceListComponent,
    PreventiveMaintenanceFormComponent,
    PreventiveMaintenanceDetailsComponent,
    PreventiveMaintenanceHistoryListComponent,
    ExternalServiceProvidersComponent,
    ExternalServiceProvidersFormComponent,
    ExternalServiceProvidersViewComponent,
    B2bContractsComponent,
    B2bContractsViewComponent,
    B2bContractsFormComponent,
    B2bContractsDriversComponent,
    B2bContractsDriverViewComponent,
    B2bContractsDriverFormComponent,
    B2bDriverDocumentsComponent,
    B2bContractsItemsComponent,
    B2bContractsVehiclesComponent,
    B2bContractsItemViewComponent,
    B2bContractsVehicleViewComponent,
    B2bContractsItemFormComponent,
    B2bContractsDriverAssignmentComponent,
    B2bPortalAccessCenterComponent,
    B2bPortalUserFormComponent,
    B2bPortalUsersHubComponent,
    B2bContractsDashboardComponent,
    B2bPortfolioHealthComponent,
    B2bOperationalNavComponent,
    B2bClaimsComponent,
    B2bClaimsFormComponent,
    B2bChargesComponent,
    B2bInvoicesComponent,
    B2bInvoiceDetailComponent,
    B2bPriceTableComponent,
    B2bBillingClosingComponent,
    B2bReservationsComponent,
    B2bAccessoriesComponent,
    B2bBillingPeriodsComponent,
    B2bTrafficFinesComponent,
    B2bSubstitutionsComponent,
    B2bReturnEventsComponent,
    B2bFleetDeliveriesComponent,
    B2bOperationalDashboardComponent,
    B2bServiceOrdersComponent,
    B2bContractPlateSelectComponent,
    UserProfileComponent,
    StatusManagementComponent,
    StatusManagementFormComponent,
    SystemLockComponent,
    NumberMaskDirective,
    CurrencyShiftMaskDirective,
    PlateMaskDirective,
    ScheduleComponent,
    PriceApprovalRangesListComponent,
    PriceApprovalRangeFormComponent,
    HrPayrollCenterComponent,
    Loca9AppComponent,
    AppointmentsExternalComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: JwtBearerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: StockAuditInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
